/**
 * @Author: v_jppjpeng
 * @Date: 2022-10-25 09:49:43
 * @Explain: 公用类方法
 */
import { notification } from '@/components/tdesign';
import { isDev } from '@/config';
import { intlGetApiError, intlGetCountry } from '@/locales';
import Cookies from 'js-cookie';
import type { Effect } from 'umi';
import { history } from 'umi';
// hooks
// router
import RouterPath from '@/router/routingPath';
// api
import { businessManageApi } from '@/api';
import * as commonServer from '@/api/common';
import { handleReturnResults } from '@/api/interfaceUtils';
// utils
import aegisMonitor from '@/utils/aegisMonitor';
import {
  doesItExist,
  isArrayLen,
  isEmptyObject,
  isNumber,
  isObject,
} from '@/utils/common/dataJudgment';
import { getActionType } from '@/utils/common/redux';
import { getNowZone } from '@/utils/format/time';
import { onSerialize } from '@/utils/format/url';
import {
  getLocalStorage,
  getSessionStorage,
  jsonParse,
  removeLocalStorage,
  removeSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from '@/utils/global';
import { smlMonitor } from '@/utils/smlMonitor';
import {
  defaultBusiness,
  defaultFilter,
  defaultFilterList,
  defaultGameFirstUser,
  defaultProjectApplyInfo,
  defaultUserInfo,
  localLanguage,
  plugInLanguage,
} from './utils/config/common';
import {
  getAbbreviationLang,
  getBusinessList,
  getCurrentBusinessItem,
} from './utils/const/common';
import type { FilterListTypes, UserAuthResData } from './utils/typing/common';
// type
import type {
  BusinessListItemIF,
  GameFirstUserInfoType,
  NeedConfigIF,
  ProjectApplyInfoIF,
  RoleJudgmentType,
  UserInfoIF,
} from '@/api/common/data.d';
import { RoleCodeEnum } from '@/api/common/data.d';
import type {
  AbbreviationLanguageType,
  LanguageType,
} from '@/definition/common/data.d';
import { StoreKeyEnum } from '@/definition/common/data.d';
import type { LoginTypeEnum } from '@/pages/adminManage/data.d';
import { LoginModeEnum, LoginStatusEnum } from '@/pages/adminManage/data.d';
import { SourceTypeEnum } from '@/pages/contactManage/addOrEdit/data.d';
import { AbbreviationLanguageEnum } from './../definition/common/data.d';
import { ReducersEnum } from './connect.d';
// config
import {
  getDefaultLeftMenu,
  shouwWhatsAppGameId,
  type MenuListType,
} from './config';
import { reportLangMonitor } from '@/utils/reportLangMonitor';

// 国家列表
export interface CountryListItem {
  text: string;
  value: string;
  timeZone: string;
  countryTwoDigitCode: string;
  countryThreeDigitCode: string;
}
// 语言列表
export interface LangUageListItem {
  text: string;
  value: string;
}
//   请求状态 init 请求前 （loading） success 请求成功 fail 请求失败
export type stateRequestStatusType = 'init' | 'success' | 'fail';

export interface CommonState {
  /** aegis 上报系统 https://aegis.woa.com/sdk/web.html#setconfig  */
  aegis: {
    /** 该方法用来修改实例配置 */
    setConfig?: ({ uin }: { uin: string }) => void;
    /** 该方法用于销毁 sdk 实例，销毁后，不再进行数据上报 */
    destroy?: () => void;
    /** 上报一条白名单日志，这两种情况这条日志才会报到后台：1、打开页面的用户在名单中；2、对应的页面发生了错误 */
    info?: (msg: string) => void;
    /** 上报了一条日志，该上报与info唯一的不同就在于，所有用户都会上报 */
    infoAll?: (msg: string) => void;
    /** 主动上报一个错误 */
    error?: (msg: string) => void;
    /** report 默认是 aegis.report 的日志类型，但是现在你可以传入任何日志类型了 */
    report?: ({
      msg,
      level,
      ext1,
      ext2,
      ext3,
      trace,
    }: {
      msg: string;
      level: string;
      ext1?: string;
      ext2?: string;
      ext3?: string;
      trace: string;
    }) => void;
    /** */
    reportEvent?: ({
      name,
      ext1,
      ext2,
      ext3,
    }: {
      name: string;
      ext1?: string;
      ext2?: string;
      ext3?: string;
    }) => void;
  };
  /** 区域管理-是否展开列表 */
  areaManageExpandedGroups: { key: string; value: boolean }[];
  /** 是否重新加载接口 - 判断 */
  reloadInterFaceFlag: boolean;
  /** 登录状态 */
  loginStatus: LoginStatusEnum;
  /** 登录类型 */
  loginType: string | LoginTypeEnum;
  /** token */
  accessToken: string;
  refreshToken: string;
  /** 当前语言环境 */
  localLanguage: LanguageType;
  plugInLanguage: AbbreviationLanguageType;
  /** 时区 */
  timezone: string;
  displayTimezone: string;
  /** 时间格式 */
  timeFormatType: string;
  /** 用户信息 */
  userInfo: UserInfoIF;
  /** 被邀请项目申请信息 */
  projectApplyInfo: ProjectApplyInfoIF;
  /** 是否获取用户状态 */
  isGetUserStatus: boolean;
  /** 拥有的角色 */
  roles: string[];
  /** 拥有的角色判断 */
  rolesJson: RoleJudgmentType;
  // 加载角色
  processRoleStatus: {
    requestStatus: stateRequestStatusType;
  };
  /** 业务信息 */
  businessList: BusinessListItemIF[];
  /** 当前业务信息 */
  currentBusiness: BusinessListItemIF;
  /** 体验项目信息 */
  demoProjectInfo: BusinessListItemIF | null;
  /** 当前业务是否开通WhatsApp */
  currentBusinessOpenWhatsApp: boolean;
  /** 左侧菜单 */
  leftMenuList: MenuListType[];
  /** 左侧菜单lock */
  leftMenuLock: boolean;
  /** 左侧菜单collapse */
  leftMenuCollapse: boolean;
  /** 顶部菜单栏 - 是否展示 */
  isFixedTopHeader: boolean;
  /** 时区 */
  timezoneList: any[];
  /** 国家 */
  countryList: CountryListItem[];
  /** 语言 */
  languageList: LangUageListItem[];
  /** 记录列表筛选（页长） */
  filterList: FilterListTypes;
  /** 是否resize smartlink-layout__body 的 style */
  isLayoutBodyStyle: boolean;
  /** 需要的配置 */
  needConfig: NeedConfigIF[];
  /** 是否重新加载获取需要的配置 */
  isConfigToBeSet: boolean;
  /** 是否显示首次公告 */
  isShowFirstTimeAnnouncement: boolean;
  /** 当前项目下首个用户加入的时间 */
  gameFirstUserInfo: GameFirstUserInfoType;
}

export type ModelType = {
  namespace: string;
  state: CommonState;
  effects: {
    getIsLogin: Effect;
    getUserInfo: Effect;
    getDemoProjectInfo: Effect;
    onSwitchItem: Effect;
    getCTLConfig: Effect;
    getConfigToBeSet: Effect;
  };
  reducers: {
    setAegis: (state: CommonState, action: { payload: any }) => any;
    onLogOut: (state: CommonState, action: { payload: any }) => any;
    setToken: (state: CommonState, action: { payload: any }) => any;
    setReloadInterFaceFlag: (
      state: CommonState,
      action: { payload: any },
    ) => any;
    setLoginStatus: (state: CommonState, action: { payload: any }) => any;
    setLoginType: (state: CommonState, action: { payload: any }) => any;
    setLocalLanguage: (state: CommonState, action: { payload: any }) => any;
    setTimezone: (state: CommonState, action: { payload: any }) => any;
    setUserInfo: (state: CommonState, action: { payload: any }) => any;
    setProjectApplyInfo: (state: CommonState, action: { payload: any }) => any;
    resetProjectApplyInfo: (
      state: CommonState,
      action: { payload: any },
    ) => any;
    setIsGetUserStatus: (state: CommonState, action: { payload: any }) => any;
    setRoles: (state: CommonState, action: { payload: any }) => any;
    setBusinessList: (state: CommonState, action: { payload: any }) => any;
    setCurrentBusiness: (state: CommonState, action: { payload: any }) => any;
    setDemoProjectInfo: (state: CommonState, action: { payload: any }) => any;
    setLeftMenuList: (state: CommonState, action: { payload: any }) => any;
    setLeftMenuLock: (state: CommonState, action: { payload: any }) => any;
    setLeftMenuCollapse: (state: CommonState, action: { payload: any }) => any;
    setIsFixedTopHeader: (state: CommonState, action: { payload: any }) => any;
    setTimezoneList: (state: CommonState, action: { payload: any }) => any;
    setCountryList: (state: CommonState, action: { payload: any }) => any;
    setLanguageList: (state: CommonState, action: { payload: any }) => any;
    setFilterList: (state: CommonState, action: { payload: any }) => any;
    setFilterItem: (
      state: CommonState,
      action: { payload: { currentUrl: string; params: any } },
    ) => any;
    resetFilterList: (state: CommonState, action: { payload: any }) => any;
    resetOneFilter: (state: CommonState, action: { payload: any }) => any;
    setTemporaryFilterItem: (
      state: CommonState,
      action: { payload: any },
    ) => any;
    setIsLayoutBodyStyle: (state: CommonState, action: { payload: any }) => any;
    setConfigToBeSet: (state: CommonState, action: { payload: any }) => any;
    setIsConfigToBeSet: (state: CommonState, action: { payload: any }) => any;
    setIsShowFirstTimeAnnouncement: (
      state: CommonState,
      action: { payload: any },
    ) => any;
    setGameFirstUserInfo: (state: CommonState, action: { payload: any }) => any;
    // 设置areaManage页面展开分组
    setAreaManageExpandedGroups: (
      state: CommonState,
      action: {
        payload: {
          key: string;
          value: boolean;
          type: 'update' | 'delete';
        };
      },
    ) => any;
  };
  // 订阅外部数据源的变化，例如路由变化、用户行为等
  subscriptions: {
    setupHistory: ({ dispatch, history }: any) => any;
  };
};

const CommonModel: ModelType = {
  namespace: 'CommonModel',
  state: {
    aegis: {},
    /* 区域管理-是否展开列表 */
    areaManageExpandedGroups: JSON.parse(
      getLocalStorage(StoreKeyEnum.AREA_MANAGE_EXPANDED_GROUPS) || '[]',
    ),
    /** 当前语言环境 */
    localLanguage,
    plugInLanguage,
    /** 时区 */
    timezone: '',
    /** 展示时区 */
    displayTimezone: '',
    /** 时间格式 - 默认 类型 2*/
    timeFormatType: '2',
    /** 是否重新加载接口 - 判断 */
    reloadInterFaceFlag: false,
    /** 登录状态 */
    loginStatus: LoginStatusEnum.INIT,
    /** 登录状态 */
    loginType: getLocalStorage(StoreKeyEnum.LOGIN_TYPE) || '',
    /** token */
    accessToken: getLocalStorage(StoreKeyEnum.ACCESS_TOKEN) || '',
    refreshToken: getLocalStorage(StoreKeyEnum.REFRESH_TOKEN) || '',
    /** 用户信息 */
    userInfo: jsonParse(getLocalStorage(StoreKeyEnum.USER_INFO)) || {
      ...defaultUserInfo,
    },
    projectApplyInfo: getSessionStorage(StoreKeyEnum.PROJECT_APPLY_INFO) || {
      ...defaultProjectApplyInfo,
    },
    /** 是否获取用户状态 */
    isGetUserStatus: true,
    /** 拥有的角色 */
    roles: [],
    /**
     * #拥有的角色判断
     * ownerAdmin - 有owner 或者 admin
     * ownerAdminManage - 有owner 或者 admin 或者 manage
     * owner - 项目所有者，拥有项目的最高权限
     * admin - 可以创建、编辑、审批campaign
     * manage - 可以创建、编辑、审批campaign
     * author - 可以创建、编辑campaign，不能审批campaign
     * operator - 运营人员，业务不能申请
     * viewer - 体验角色，用户不需要申请
     */
    rolesJson: {
      isInternal: false,
      isOwnerAdmin: false,
      isOwnerAdminManage: false,
      isOwner: false,
      isAdmin: false,
      isManage: false,
      isAuthor: false,
      isOperator: false,
      isViewer: false,
    },
    processRoleStatus: {
      //   请求状态 init 请求前 （loading） success 请求成功 fail 请求失败
      requestStatus: 'init',
    },
    /** 业务信息 */
    businessList: [],
    /** 当前业务信息 */
    currentBusiness: jsonParse(
      getLocalStorage(StoreKeyEnum.CURRENT_BUSINESS),
    ) || { ...defaultBusiness },
    /** 体验项目 */
    demoProjectInfo: null,
    /** 当前业务是否开通WhatsApp */
    currentBusinessOpenWhatsApp: false,
    /** 左侧菜单 */
    leftMenuList: (() => {
      const currentBusiness = jsonParse(
        getLocalStorage(StoreKeyEnum.CURRENT_BUSINESS),
      ) || { ...defaultBusiness };
      return getDefaultLeftMenu(currentBusiness);
    })(),
    /** 左侧菜单Lock true - 锁 false - 不锁 */
    leftMenuLock:
      jsonParse(getLocalStorage(StoreKeyEnum.LEFT_MENU_LOCK)) ?? true,
    /** 左侧菜单collapse */
    leftMenuCollapse: jsonParse(
      getLocalStorage(StoreKeyEnum.LEFT_MENU_COLLAPSE),
    ),
    /** 顶部菜单栏 - 是否fixed */
    isFixedTopHeader: true,
    /** 时区列表 */
    timezoneList: [],
    /** 国家列表 */
    countryList: [],
    /** 语言列表 */
    languageList: [],
    /** 记录列表筛选 */
    filterList: jsonParse(getLocalStorage(StoreKeyEnum.FILTER_LIST)) || {
      ...defaultFilterList,
    },
    /** 是否resize smartlink-layout__body 的 style */
    isLayoutBodyStyle: false,
    /** 需要的配置 */
    needConfig: [],
    /** 是否重新加载获取需要的配置 */
    isConfigToBeSet: false,
    /** 当前项目下首个用户加入的时间 */
    gameFirstUserInfo: { ...defaultGameFirstUser },
    /** 是否展示首次公告 */
    isShowFirstTimeAnnouncement: false,
  },
  effects: {
    // 获取是否登录
    *getIsLogin({ payload }, { call, put }): any {
      try {
        const result = yield call(commonServer.getIsLogin, payload);
        const { code, data, msg } = result || {};
        let flag = false;

        if (data) {
          if (isNumber(data)) {
            flag = code === 0 && data === 1;
          }
          if (isObject(data)) {
            flag = code === 0 && data.state === 1;
          }
        }
        yield put({
          type: getActionType(ReducersEnum.SET_LOGIN_STATUS),
          payload: flag
            ? LoginStatusEnum.SIGNED_IN
            : LoginStatusEnum.LOGIN_FAIL,
        });

        if (!flag) {
          const description = intlGetApiError(code) || '';
          if (description) {
            notification.error({ description });
          }
        }
      } catch (e) {
        yield put({
          type: getActionType(ReducersEnum.SET_LOGIN_STATUS),
          payload: LoginStatusEnum.LOGIN_FAIL,
        });
      }
    },
    // 获取当前登录用户信息
    *getUserInfo({ payload }, { call, select, put }): any {
      try {
        const result = yield call(commonServer.getUserInfo, payload);
        let resData: UserAuthResData = {
          accountInfo: { ...defaultUserInfo },
          roleInfo: [],
          businessList: [],
          // 是否开通in-game
          inGame: false,
          // 预测标签开关
          predictiveTagSwitch: false,
        };
        handleReturnResults({
          params: result,
          successCallBack: (res: any) => {
            if (res?.data) {
              resData = res?.data;
            }
          },
        });
        const { accountId, accountName, email } = resData.accountInfo;
        // aegis setConfig uin
        aegisMonitor.setConfig({ uin: email });

        // 账号信息
        yield put({
          type: getActionType(ReducersEnum.SET_USER_INFO),
          payload: resData?.accountInfo,
        });

        // 业务列表
        const newBusinessList = getBusinessList(resData);
        // 当前业务信息
        const currentBusinessItem = getCurrentBusinessItem({
          businessList: newBusinessList,
          accountId,
        });

        // 业务列表
        yield put({
          type: getActionType(ReducersEnum.SET_BUSINESS_LIST),
          payload: newBusinessList,
        });
        // 当前业务信息
        yield put({
          type: getActionType(ReducersEnum.SET_CURRENT_BUSINESS),
          payload: currentBusinessItem,
        });
        // 当前角色
        yield put({
          type: getActionType(ReducersEnum.SET_ROLES),
          payload: currentBusinessItem?.roles,
        });
        yield put({
          type: getActionType(ReducersEnum.SET_RELOAD_INTERFACE_FLAG),
          payload: true,
        });
        // 当有业务信息才跑下面的逻辑
        if (currentBusinessItem && currentBusinessItem.gameId) {
          // 是否重新刷新需要的配置
          yield put({
            type: getActionType(ReducersEnum.SET_IS_CONFIG_TO_BE_SET),
            payload: true,
          });
        }

        // sdk 参数配置
        const state = yield select((state: any) => state);
        // sml 内部 http 需要的header信息
        smlMonitor?.setReportApi({
          accountId,
          uin: email,
          serviceType: currentBusinessItem?.gameId,
          token: state.CommonModel.accessToken,
        });
        // 初始化页面登录之后 会进入获取角色信息，切换语言会 执行 location.refresh 刷新页面 进入这里 所有放在这里执行记录用户语言
        reportLangMonitor?.initRecordUserLangBehavior?.(state.CommonModel.localLanguage)

        // 根据业务是否开启WhatsApp更新“选择导入联系人的来源”默认高亮配置
        yield put({
          type: ReducersEnum.SET_ADD_CONTECT_SOURCE_TYPE,
          payload: {
            sourceType: SourceTypeEnum.uploadAFile,
            path: state.CommonModel.currentBusinessOpenWhatsApp
              ? RouterPath.CONTACT_MANAGE_ADD_EDIT_IMPORT_CSV_WHATSAPP_UPLOAD_FILE
              : RouterPath.CONTACT_MANAGE_ADD_EDIT_IMPORT_CSV_UPLOAD_FILE,
          },
        });
      } catch (error: any) {
        console.error('getUserInfo error', error);
      }
    },
    // 获取体验项目信息
    *getDemoProjectInfo({ payload }, { call, put }): any {
      const result = yield call(businessManageApi.getDemoProject, payload);
      if (result.code === 0) {
        const gameItem = result?.data;
        yield put({
          type: getActionType(ReducersEnum.SET_DEMO_PROJECT_INFO),
          payload: {
            gameId: gameItem.projectId,
            gameZh: gameItem.projectNameZh,
            gameEn: gameItem.projectNameEn,
            isAuthorize: gameItem.isAuthorize,
            roles: [RoleCodeEnum.VIEWER],
          },
        });
      }
    },
    // 切换项目
    *onSwitchItem({ payload }, { put }): any {
      const { gameData } = payload;
      yield put({
        type: getActionType(ReducersEnum.SET_CURRENT_BUSINESS),
        payload: gameData,
      });
      yield put({
        type: getActionType(ReducersEnum.SET_ROLES),
        payload: gameData?.roles || [],
      });
    },
    // 获取国家、时区、语言
    *getCTLConfig({ payload }, { call, put }): any {
      try {
        const result = yield call(commonServer.getCTLConfig, payload);

        const { country, language } = result?.data || {
          country: [],
          language: [],
        };
        const timezoneArr: any[] = Array.from(
          new Set(
            country.map((item: CountryListItem) => {
              const str = item.timeZone.split('UTC');
              return str?.[1];
            }),
          ),
        ).sort();
        const timezone: any[] = [];
        timezoneArr.forEach((tz) => {
          const value = tz ? `UTC${tz}` : '';
          const obj: { text: any[]; value: string } = {
            text: [],
            value,
          };
          country.forEach((item: CountryListItem) => {
            if (item.timeZone === value) {
              obj.text.push(intlGetCountry(item.value));
            }
          });
          if (value) {
            timezone.push(obj);
          }
        });
        yield put({
          type: getActionType(ReducersEnum.SET_TIMEZONE_LIST),
          payload: timezone,
        });
        yield put({
          type: getActionType(ReducersEnum.SET_COUNTRY_LIST),
          payload: country,
        });
        yield put({
          type: getActionType(ReducersEnum.SET_LANGUAGE_LIST),
          payload: language,
        });
      } catch (error: any) {
        console.error('getCTLConfig error');
      }
    },
    // 获取需要的配置
    *getConfigToBeSet({ payload }, { call, put }): any {
      const result = yield call(commonServer.getConfigToBeSet, payload);
      const { code, data } = result;
      if (code === 0) {
        yield put({
          type: getActionType(ReducersEnum.SET_CONFIG_TO_BE_SET),
          payload: data?.quickSetup.filter((item: any) => item.status !== 1),
        });
      }
      yield put({
        type: getActionType(ReducersEnum.SET_IS_CONFIG_TO_BE_SET),
        payload: false,
      });
    },
  },
  reducers: {
    /* 设置区域管理-是否展开列表 */
    setAreaManageExpandedGroups(state, action) {
      if (action.payload.type === 'update') {
        const curr = state.areaManageExpandedGroups.find(
          (item) => item.key === action.payload.key,
        );
        if (curr) {
          curr.value = action.payload.value;
        } else {
          state.areaManageExpandedGroups.push({
            key: action.payload.key,
            value: action.payload.value,
          });
        }
      } else if (action.payload.type === 'delete') {
        state.areaManageExpandedGroups = state.areaManageExpandedGroups.filter(
          (item) => item.key !== action.payload.key,
        );
      }
      setLocalStorage(
        StoreKeyEnum.AREA_MANAGE_EXPANDED_GROUPS,
        state.areaManageExpandedGroups,
      );
      return {
        ...state,
        areaManageExpandedGroups: state.areaManageExpandedGroups,
      };
    },
    // aegis 上报
    setAegis(state, action) {
      return {
        ...state,
        aegis: action.payload,
      };
    },
    // 设置当前语言环境
    setLocalLanguage(state, action) {
      const localLanguageVal: LanguageType = action.payload;
      // tea-design语言设置,例如zh
      const plugInLanguageVal: AbbreviationLanguageType =
        getAbbreviationLang(localLanguageVal);
      // 时间格式类型
      const timeFormatType =
        plugInLanguageVal === AbbreviationLanguageEnum.ZH ? 1 : 2;
      // cookies 参数
      const cookiesParams = {
        domain: isDev ? '' : 'smartlink.intlgame.com',
      };
      // 全局存储语言设置，例如zh-CN
      setLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE, localLanguageVal);
      Cookies.set(StoreKeyEnum.LOCAL_LANGUAGE, localLanguageVal, cookiesParams);

      setLocalStorage(StoreKeyEnum.DESIGN_LANGUAGE, plugInLanguageVal);
      Cookies.set(
        StoreKeyEnum.DESIGN_LANGUAGE,
        plugInLanguageVal,
        cookiesParams,
      );

      setLocalStorage(StoreKeyEnum.TIME_FORMAT_TYPE, timeFormatType);

      return {
        ...state,
        plugInLanguage: plugInLanguageVal,
        localLanguage: localLanguageVal,
        timeFormatType,
      };
    },
    // 设置时区
    setTimezone(state, action) {
      const val = action.payload || getNowZone().halfFormatUtcZone;
      /** eg: UTC+08:45 - 全站统一格式 */
      const timezoneString = val;
      /** eg: UTC+8:45 - 这个用于显示,还有创建活动需要使用,后续需要变更 */
      const displayTimezone = val.replace(/([+-])0/g, '$1');

      setLocalStorage(StoreKeyEnum.LOCAL_TIMEZONE, timezoneString);

      return {
        ...state,
        timezone: timezoneString,
        displayTimezone,
      };
    },
    // 退出登录
    onLogOut(state, action) {
      const { origin, pathname, href } = window.location;
      const searchParams = {
        ...action.payload,
      };

      let returnUrl = href;

      // 当如果是(登录页,欢迎页)时，记录首页为回跳
      if (
        doesItExist(pathname, RouterPath.LOGIN) ||
        doesItExist(pathname, RouterPath.NO_PERMISSION) ||
        doesItExist(pathname, RouterPath.NO_AUTH_PAGE) ||
        doesItExist(pathname, RouterPath.WELCOME_PAGE)
      ) {
        returnUrl = origin;
      }
      setSessionStorage({
        name: StoreKeyEnum.RETURN_URL,
        content: returnUrl,
      });
      const clearArr = [
        'accessToken0628',
        StoreKeyEnum.ACCESS_TOKEN,
        StoreKeyEnum.REFRESH_TOKEN,
        StoreKeyEnum.LOGIN_TYPE,
        StoreKeyEnum.USER_INFO,
        StoreKeyEnum.CURRENT_BUSINESS,
        StoreKeyEnum.HOME_TODOLIST_STATUS_INFO,
        StoreKeyEnum.FILTER_LIST,
        StoreKeyEnum.LABEL_MANA_PREDICTIVE_ALERT,
        StoreKeyEnum.NEWSLETTER_DETAILS,
        StoreKeyEnum.NEWSLETTER_IS_DEAFT,
        StoreKeyEnum.AREA_MANAGE_EXPANDED_GROUPS,
      ];
      clearArr.forEach((key) => {
        removeLocalStorage(key);
      });
      removeSessionStorage(StoreKeyEnum.GAME_ID);
      const params = onSerialize(searchParams);
      const url = RouterPath.LOGIN + (params ? `?${params}` : '');

      history.replace(url);
      return {
        ...state,
        accessToken: '',
        refreshToken: '',
        loginStatus: LoginStatusEnum.INIT,
        loginMode: LoginModeEnum.default, // 退出登录时返回初始化(模式)登录模式
        loginType: '',
        userInfo: { ...defaultUserInfo },
        isGetUserStatus: true,
        reloadInterFaceFlag: false,
        businessList: [],
        currentBusiness: { ...defaultBusiness },
        filterList: {
          ...defaultFilterList,
        },
        // 创建活动需要完成的配置
        needConfig: [],
      };
    },
    // 设置是否重新加载接口判断
    setReloadInterFaceFlag(state, action) {
      return {
        ...state,
        reloadInterFaceFlag: action.payload,
      };
    },
    // 登录状态
    setLoginStatus(state, action) {
      return {
        ...state,
        loginStatus: action.payload,
      };
    },
    // 登录类型
    setLoginType(state, action) {
      const loginType = action.payload;
      setLocalStorage(StoreKeyEnum.LOGIN_TYPE, loginType);
      return {
        ...state,
        loginType,
      };
    },
    // 设置token
    setToken(state, action) {
      const { accessToken, refreshToken } = action.payload;
      setLocalStorage(StoreKeyEnum.ACCESS_TOKEN, accessToken);
      return {
        ...state,
        accessToken,
      };
    },
    // 设置用户信息
    setUserInfo(state, action) {
      const userInfo = {
        ...state.userInfo,
        ...action.payload,
      };
      setLocalStorage(StoreKeyEnum.USER_INFO, userInfo);
      return {
        ...state,
        userInfo,
      };
    },
    // 设置被邀请-项目申请信息
    setProjectApplyInfo(state, action) {
      const projectApplyInfo = action.payload;
      setSessionStorage({
        name: StoreKeyEnum.PROJECT_APPLY_INFO,
        content: projectApplyInfo,
      });
      return {
        ...state,
        projectApplyInfo,
      };
    },
    // 重置项目申请信息
    resetProjectApplyInfo(state, action) {
      removeSessionStorage(StoreKeyEnum.PROJECT_APPLY_INFO);
      return {
        ...state,
        projectApplyInfo: {
          ...defaultProjectApplyInfo,
        },
      };
    },
    // 设置是否获取用户状态
    setIsGetUserStatus(state, action) {
      return {
        ...state,
        isGetUserStatus: action.payload,
      };
    },
    // 设置角色
    setRoles(state, action) {
      const roles: string = action.payload;
      const isInternal = doesItExist(RoleCodeEnum.INTERNAL, roles);
      const isOwner = doesItExist(RoleCodeEnum.OWNER, roles);
      const isAdmin = doesItExist(RoleCodeEnum.ADMIN, roles);
      const isManage = doesItExist(RoleCodeEnum.MANAGER, roles);
      const isAuthor = doesItExist(RoleCodeEnum.AUTHOR, roles);
      const isOperator = doesItExist(RoleCodeEnum.OPERATOR, roles);
      const isViewer = doesItExist(RoleCodeEnum.VIEWER, roles);
      return {
        ...state,
        roles,
        rolesJson: {
          isInternal,
          isOwnerAdminManage: isInternal || isOwner || isAdmin || isManage,
          isOwnerAdmin: isInternal || isOwner || isAdmin,
          isOwner: isInternal || isOwner,
          isAdmin: isInternal || isAdmin,
          isManage: isInternal || isManage,
          isAuthor,
          isOperator: isInternal || isOperator,
          isViewer,
        },
        processRoleStatus: {
          requestStatus: 'finish',
        },
      };
    },
    // 设置业务信息
    setBusinessList(state, action) {
      return {
        ...state,
        businessList: action.payload,
      };
    },
    // 设置当前业务信息
    setCurrentBusiness(state, action) {
      const currentData = action.payload || defaultBusiness;
      const storeKey: any =
        StoreKeyEnum.CURRENT_BUSINESS + state?.userInfo?.accountId;
      if (currentData && +currentData.gameId > 0) {
        setLocalStorage(storeKey, currentData);
        setLocalStorage(StoreKeyEnum.CURRENT_BUSINESS, currentData);
      }
      // 切换业务同时初始化列表筛选信息
      removeLocalStorage(StoreKeyEnum.FILTER_LIST);
      // 基于新的业务信息重新生成左侧菜单
      const newLeftMenuList = getDefaultLeftMenu(
        currentData || { ...defaultBusiness },
      );

      return {
        ...state,
        dynamicIndexInfo: 0, // 切换业务后 初始化Report动态指标信息
        currentBusiness: currentData,
        leftMenuList: newLeftMenuList,
        // 当前业务是否开通WhatsApp
        currentBusinessOpenWhatsApp: shouwWhatsAppGameId.includes(
          currentData?.gameId,
        ),
      };
    },
    // 设置demo项目信息
    setDemoProjectInfo(state, action) {
      return {
        ...state,
        demoProjectInfo: action.payload,
      };
    },
    // 设置左侧菜单
    setLeftMenuList(state, action) {
      return {
        ...state,
        leftMenuList: action.payload,
      };
    },
    // 设置左侧菜单lock
    setLeftMenuLock(state, action) {
      setLocalStorage(StoreKeyEnum.LEFT_MENU_LOCK, action.payload);
      return {
        ...state,
        leftMenuLock: action.payload,
      };
    },
    // 设置左侧菜单collapse
    setLeftMenuCollapse(state, action) {
      setLocalStorage(StoreKeyEnum.LEFT_MENU_COLLAPSE, action.payload);
      return {
        ...state,
        leftMenuCollapse: action.payload,
      };
    },
    // 设置是否展示顶部菜单
    setIsFixedTopHeader(state, action) {
      return {
        ...state,
        isFixedTopHeader: action.payload,
      };
    },
    // 设置时区列表
    setTimezoneList(state, action) {
      return {
        ...state,
        timezoneList: action.payload,
      };
    },
    // 设置国家列表
    setCountryList(state, action) {
      return {
        ...state,
        countryList: action.payload,
      };
    },
    // 设置语言列表
    setLanguageList(state, action) {
      return {
        ...state,
        languageList: action.payload,
      };
    },
    // 设置列表筛选
    setFilterList(state, action) {
      const filterList = {
        ...state.filterList,
      };
      const { currentUrl, params } = action.payload;

      if (currentUrl && !isEmptyObject(params) && filterList[currentUrl]) {
        filterList[currentUrl] = {
          ...params,
          pageIndex: params?.pageIndex || 1,
        };
      }

      // setLocalStorage(StoreKeyEnum.FILTER_LIST, storeFilterList);
      return {
        ...state,
        filterList,
      };
    },
    // 设置指定列表搜索参数
    setFilterItem(state, action) {
      const filterList = {
        ...state.filterList,
      };
      const { currentUrl, params } = action.payload;

      if (currentUrl && !isEmptyObject(params) && filterList[currentUrl]) {
        filterList[currentUrl] = {
          ...params,
        };
      }
      return {
        ...state,
        filterList,
      };
    },
    // 重置所有列表筛选
    resetFilterList(state) {
      removeLocalStorage(StoreKeyEnum.FILTER_LIST);
      return {
        ...state,
        filterList: { ...defaultFilterList },
      };
    },
    // 重置某一个筛选
    resetOneFilter(state, action) {
      const { key } = action.payload;
      const filterList: any = {
        ...state.filterList,
      };
      if (key) {
        filterList[key] = { ...defaultFilter };
      }
      console.log('filterList', filterList);

      return {
        ...state,
        filterList,
      };
    },
    // 监听主路由信息发生变化，初始化临时存储的筛选信息
    setTemporaryFilterItem(state, action) {
      const { primaryRoutePart } = action.payload;
      let params = {};
      const { filterList } = state;
      // filterList非空则监听路，由若主路径变化根据unSearchInfo将filterList其它路由存储不做筛选功能的key更新为默认信息(即页面不回显&接口不过滤)
      for (const key in filterList) {
        let obj: any = {};
        if (
          !key.includes(primaryRoutePart) &&
          isArrayLen(filterList[key]?.unSearchInfo)
        ) {
          filterList[key].unSearchInfo?.map((item) => {
            obj = {
              ...filterList[key],
              ...obj,
              [item.key]: item.defaultValue,
            };
          });
        }
        if (!isEmptyObject(obj)) {
          if (obj?.unSearchInfo) {
            delete obj.unSearchInfo;
          }
          params = { ...params, [key]: obj };
        }
      }

      return {
        ...state,
        filterList: {
          ...filterList,
          ...params,
        },
      };
    },
    /** 设置是否resize smartlink-layout__body 的 style */
    setIsLayoutBodyStyle(state, action) {
      return {
        ...state,
        isLayoutBodyStyle: action.payload,
      };
    },
    /** 设置需要设置的配置 **/
    setConfigToBeSet(state, action) {
      return {
        ...state,
        needConfig: action.payload,
      };
    },
    // 设置是否重新加载 getConfigToBeSet
    setIsConfigToBeSet(state, action) {
      return {
        ...state,
        isConfigToBeSet: action.payload,
      };
    },
    // 设置当前项目下首个用户加入的时间
    setGameFirstUserInfo(state, action) {
      const params = {
        ...state,
        gameFirstUserInfo: action.payload,
      };
      return params;
    },
    setIsShowFirstTimeAnnouncement(state, action) {
      return {
        ...state,
        isShowFirstTimeAnnouncement: action.payload,
      };
    },
  },
  subscriptions: {
    setupHistory({ dispatch, history }) {
      // // 监听路由变化，更新filterList
      return history.listen((localtion: any) => {
        // 当前页面路由
        const pathname = localtion?.location?.pathname;
        // 截取当前菜单对应路由主要信息部分
        const primaryRoutePart = pathname.split('/').slice(0, 3).join('/');
        // 跳过路由表
        const skipRoutes = [
          RouterPath.LOGIN,
          RouterPath.REGISTER,
          RouterPath.FORGOT_PASSWORD,
          RouterPath.WELCOME_PAGE,
          RouterPath.VIEW_TEMPLATE,
          RouterPath.NO_PERMISSION,
          RouterPath.TRANSFER,
          '/',
        ];

        // 监听主路由信息发生变化，初始化临时存储的筛选信息
        if (pathname && !skipRoutes.some((item) => item === pathname)) {
          dispatch({
            type: getActionType(ReducersEnum.SET_TEMPORARY_FILTER_ITEM),
            payload: { primaryRoutePart },
          });
        }
      });
    },
  },
};

export default CommonModel;
